<template>
  <div class="sign-documents-index">
    <Filters
      :filters="signDocumentsFilters"
      filter-key="signDocuments"
      :initial-data="filtersStatus.signDocuments"
      :loading="filtersLoading"
    />
    <TableWrapper
      :tableColumns="signDocumentsColumns"
      :enableSelection="true"
      :custom-bulk-action-options="bulkAction"
      custom-bulk-action
      table-key="signDocuments"
      row-key="id"
      @signDocument="(val) => signDocuments(val, true)"
      @viewDocumentPdf="openPdf"
      @downloadSignedPdf="downloadPdf"
    />
  </div>
</template>

<script>
  import moment from 'moment';
  import { mapActions, mapGetters } from 'vuex';
  import { signDocumentsFilters, signDocumentsColumns } from '@/config';
  import { signDocumentsApi } from '@/api';
  import Filters from '@/components/Filters.vue';
  import TableWrapper from '@/components/TableWrapper.vue';
  import fileSaver from 'file-saver';
  import abortControllerMixin from '@/mixins/abortControllerMixin';
  import filterLoadingMixin from '@/mixins/filterLoadingMixin';
  import axios from 'axios';

  export default {
    name: 'SignDocumentsIndex',
    components: {
      TableWrapper,
      Filters,
    },
    mixins: [abortControllerMixin, filterLoadingMixin],
    data() {
      return {
        signDocumentsFilters,
        signDocumentsColumns,
        bulkAction: {
          label: this.$t('signDocuments.signMany'),
          action: this.signDocuments,
        },
      };
    },
    computed: {
      ...mapGetters(['tableFilters', 'filtersStatus']),
    },
    methods: {
      ...mapActions([
        'updateTableRows',
        'updateTableCount',
        'parseQueryStringStatus',
      ]),
      async tableRows() {
        const signal = this.createAbortController();
        this.filtersLoading = true;
        let aborted = false;
        try {
          const documentsIndex = await signDocumentsApi.documentsIndex(
            this.tableFilters('signDocuments'),
            this.filtersStatus.signDocuments,
            { signal }
          );
          const documentsCount = await signDocumentsApi.documentsCount(
            this.filtersStatus.signDocuments,
            { signal }
          );

          this.updateTableRows({
            tableKey: 'signDocuments',
            rows: documentsIndex.data,
          });
          this.updateTableCount({
            tableKey: 'signDocuments',
            count: documentsCount.data.count,
          });
        } catch (error) {
          if (axios.isCancel(error)) {
            aborted = true;
            return;
          }
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        } finally {
          if (!aborted) {
            this.filtersLoading = false;
          }
        }
      },
      signDocuments(selection, single = false) {
        // Loading spinner
        let loading;
        this.$prompt(
          this.$t('signDocuments.sendOtp'),
          this.$t('signDocuments.verify'),
          {
            confirmButtonText: this.$t('signDocuments.send'),
            cancelButtonText: this.$t('signDocuments.cancel'),
          }
        )
          .then(async ({ value }) => {
            loading = this.$loading({
              lock: true,
              background: 'rgba(48,53,82,0.9)',
            });
            let documentsToSign = [];
            if (single) {
              documentsToSign.push(selection.id);
            } else {
              documentsToSign = selection
                .filter((item) => !item.signedBy)
                .map((item) => item.id);
            }
            await signDocumentsApi.signDocuments(documentsToSign, value);
            this.tableRows();
            loading.close();
            this.$message({
              type: 'success',
              message: this.$t('signDocuments.signedDocuments'),
            });
          })
          .catch((error) => {
            if (error.response) {
              loading.close();
              this.$notify({
                showClose: true,
                type: 'error',
                message: error.response.data.error.message,
              });
            }
          });
      },
      async openPdf(document) {
        const unsignedPdf = await signDocumentsApi.getUnsignedPdf(document.id);
        window.open(URL.createObjectURL(unsignedPdf.data));
      },
      async downloadPdf(document) {
        const signedPdf = await signDocumentsApi.getSignedPdf(document.id);
        fileSaver.saveAs(
          signedPdf.data,
          this.$t('signDocuments.fileName', {
            date: moment(signedPdf.signedAt).format('YYYY-MM-DD'),
          })
        );
      },
    },
    created() {
      this.parseQueryStringStatus(this.$route.query);
      this.tableRows();
    },
    beforeRouteUpdate(to, from, next) {
      this.parseQueryStringStatus(to.query);
      this.tableRows();
      next();
    },
  };
</script>
